import * as React from 'react';
import styled from 'styled-components';
import {getImageByPreview} from 'api/library';
import BoxContainer from 'containers/BoxContainer';
import {transition} from '../helpers/css';
import Tag, {Container as TagContainer} from './Tag';
import {RecipeObject} from '../api/recipe/types';

const Tags = styled.div`
  flex-wrap: wrap;
  display: flex;
  
  ${TagContainer} {
    margin-top: 2px;
    margin-right: 4px;
    margin-bottom: 2px;
  }
`;

export interface ExercisePreviewProps extends RecipeObject {
  renderControls?: JSX.Element;
}

const Container = styled(BoxContainer)`
  margin-bottom: 8px;
  border-radius: 5px;
  box-sizing: border-box;
  padding: 4px;
  
  gap: 20px;
  align-items: flex-start;
  grid-template-columns: 160px 1fr max-content;
  display: grid;
`;

const Preview = styled.img`
  border-radius: 5px;
  object-fit: cover;
  height: 88px;
  width: 100%;
`;

const Content = styled.div`
  display: grid;
  justify-content: flex-start;
`;

const Title = styled.a`
  margin: 8px 0;
  display: inline-block;
  ${transition()};
  
  line-height: 20px;
  font-weight: 600;
  font-size: 16px;
  
  &:hover,
  &:focus-visible {
    color: ${({theme}) => theme.colors.primary};
  }
`;

const Controls = styled.div`
  box-sizing: border-box;
  padding: 4px;
`;

/**
 *
 * @param {ExercisePreviewProps} props
 * @return {React.ReactElement<ExercisePreviewProps>}
 * @constructor
 */
export default function ExercisePreview(
    props: ExercisePreviewProps,
): React.ReactElement<ExercisePreviewProps> {
  /**
   * Render preview.
   * @return {JSX.Element}
   */
  function renderPreview(): JSX.Element {
    return (
      <Preview
        src={getImageByPreview(props.preview)}
        alt={props.title}
      />
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render tags.
     * @return {JSX.Element}
     */
    function renderTags(): JSX.Element {
      return (
        <Tags>
          {props.tags?.map((tag, index) => (
            <Tag key={index}>
              {tag}
            </Tag>
          ))}
        </Tags>
      );
    }

    /**
     * Render title.
     * @return {JSX.Element}
     */
    function renderTitle(): JSX.Element {
      return (
        <Title href={`/recipes/${props.id}/view`}>
          {props.title}
        </Title>
      );
    }

    return (
      <Content>
        {renderTags()}
        {renderTitle()}
      </Content>
    );
  }

  /**
   * Render controls
   * @return {JSX.Element | undefined}
   */
  function renderControls(): JSX.Element | undefined {
    return props.renderControls ? (
      <Controls>
        {props.renderControls}
      </Controls>
    ) : undefined;
  }

  return (
    <Container>
      {renderPreview()}
      {renderContent()}
      {renderControls()}
    </Container>
  );
}
